'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { OverviewTab, RequirementsTab, WerkcurveTab, UseCasesTab, PlanTab, hasPlan } from './client-report';
import { CompletionProgress } from './CompletionProgress';
import DownloadPdfButton from '@/components/shared/DownloadPdfButton';
import type { OutputData, TabType } from './client-report';
import { withBase } from '@/lib/base-path'
import { pushEvent, pushPageContext } from '@/lib/tracking'

interface ClientReportProps {
  sessionId: string;
  sessionToken?: string;
  contactName?: string;
  companyName?: string;
}

// LLM generation runs two sequential Claude calls; 30s was too tight and users
// hit the error screen while generation was still running. ~120s gives headroom.
const RETRY_DELAYS = [2000, 3000, 5000, 7000, 10000, 15000, 20000, 25000, 30000];
const MAX_ATTEMPTS = RETRY_DELAYS.length;

export function ClientReport({ sessionId, sessionToken, contactName, companyName }: ClientReportProps) {
  const [data, setData] = useState<OutputData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  useEffect(() => {
    pushPageContext('ai_discovery_report');
  }, []);

  const reportTrackedRef = useRef(false);
  useEffect(() => {
    if (!data || reportTrackedRef.current) return;
    reportTrackedRef.current = true;
    pushEvent('ai_discovery_report_view', {
      werkcurve_level: data.werkcurve?.level,
      use_case_count: data.werkcurve?.use_cases.length,
    });
  }, [data]);

  const fetchOutput = useCallback(async (attemptNum: number) => {
    try {
      const headers: Record<string, string> = {};
      if (sessionToken) headers['X-Session-Token'] = sessionToken;
      const response = await fetch(withBase(`/api/interview/session/${sessionId}/output`), { headers });
      if (response.ok) {
        const result = await response.json();
        if (result.requirements || result.werkcurve) {
          setData({
            requirements: result.requirements,
            werkcurve: result.werkcurve,
            phaseQuality: result.phaseQuality || undefined,
          });
          setIsLoading(false);
          setFetchError(false);
          return;
        }
      }

      // No data yet — retry if we have attempts left
      if (attemptNum < MAX_ATTEMPTS - 1) {
        setAttempt(attemptNum + 1);
        setTimeout(() => fetchOutput(attemptNum + 1), RETRY_DELAYS[attemptNum]);
      } else {
        setIsLoading(false);
        setFetchError(true);
      }
    } catch (err) {
      console.error('Failed to fetch output:', err);
      if (attemptNum < MAX_ATTEMPTS - 1) {
        setAttempt(attemptNum + 1);
        setTimeout(() => fetchOutput(attemptNum + 1), RETRY_DELAYS[attemptNum]);
      } else {
        setIsLoading(false);
        setFetchError(true);
      }
    }
  }, [sessionId, sessionToken]);

  useEffect(() => {
    fetchOutput(0);
  }, [fetchOutput]);

  const handleManualRetry = () => {
    setIsLoading(true);
    setFetchError(false);
    setAttempt(0);
    fetchOutput(0);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-off-white min-h-screen flex flex-col items-center justify-center">
        <CompletionProgress
          stage="analyzing"
          subtitle={
            attempt > 0
              ? 'Nog even geduld, we zijn er bijna.'
              : undefined
          }
        />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center bg-off-white min-h-screen flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-orange-cta/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-cta" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="font-display font-light text-3xl text-off-black mb-2">Het rapport is nog niet klaar</h2>
        <p className="text-off-black/60 mb-6">Het samenstellen duurt langer dan normaal. Probeer het over een minuut opnieuw.</p>
        <button
          onClick={handleManualRetry}
          className="px-6 py-2 bg-orange-cta hover:bg-orange-hover text-white rounded-xl transition-colors"
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: 'Overzicht' },
    { id: 'werkcurve', label: 'Werkcurve' },
    { id: 'use_cases', label: 'Use cases' },
    ...(data?.werkcurve && hasPlan(data.werkcurve) ? [{ id: 'plan' as const, label: 'Plan' }] : []),
    { id: 'requirements', label: 'Behoeftes' },
  ];
  const openTab = (tab: TabType) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:p-8 bg-off-white min-h-screen print-show-all">
      {/* Header */}
      <div className="mb-10 pt-4">
        <Image src={withBase("/logo.svg")} alt="SPAIK" width={96} height={28} className="h-7 w-auto mb-10" />
        <p className="text-sm text-off-black/50 mb-2">AI Discovery rapport</p>
        <h1 className="font-display font-thin text-4xl sm:text-5xl leading-[1.05] text-off-black">
          {companyName ? `Wat we hoorden van ${companyName}` : 'Wat we hoorden in het gesprek'}
        </h1>
        <p className="text-off-black/70 mt-4">
          Bedankt{contactName ? `, ${contactName.split(' ')[0]}` : ''}. Hieronder staat wat we uit het gesprek
          hebben gehaald, waar jullie nu staan en wat een logische eerste stap is.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-off-black/10 mb-6" role="tablist" data-tab-nav>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            onClick={() => setActiveTab(tab.id)}
            aria-selected={activeTab === tab.id}
            className={`px-4 py-2 -mb-px text-sm whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'text-off-black font-medium border-b-2 border-orange-cta'
                : 'text-off-black/50 hover:text-off-black/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content — each panel gets .tab-panel so print CSS can render all
          three stacked on paper while the browser shows only the active one. */}
      {data && (
        <>
          <div
            className={`tab-panel ${activeTab === 'overview' ? '' : 'hidden'}`}
            role="tabpanel"
          >
            <OverviewTab data={data} onOpenTab={openTab} />
          </div>
          {data.requirements && (
            <div
              className={`tab-panel ${activeTab === 'requirements' ? '' : 'hidden'}`}
              role="tabpanel"
            >
              <RequirementsTab requirements={data.requirements} />
            </div>
          )}
          {data.werkcurve && (
            <>
              <div
                className={`tab-panel ${activeTab === 'werkcurve' ? '' : 'hidden'}`}
                role="tabpanel"
              >
                <WerkcurveTab werkcurve={data.werkcurve} />
              </div>
              <div
                className={`tab-panel ${activeTab === 'use_cases' ? '' : 'hidden'}`}
                role="tabpanel"
              >
                <UseCasesTab werkcurve={data.werkcurve} />
              </div>
              {hasPlan(data.werkcurve) && (
                <div
                  className={`tab-panel ${activeTab === 'plan' ? '' : 'hidden'}`}
                  role="tabpanel"
                >
                  <PlanTab werkcurve={data.werkcurve} />
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Footer CTA */}
      <div className="mt-10 bg-off-black text-off-white rounded-2xl p-6 sm:p-8">
        <h2 className="font-display font-thin text-3xl sm:text-4xl leading-tight">Dit rapport samen bespreken?</h2>
        <p className="text-off-white/70 mt-3 max-w-xl">
          Plan een gesprek met een van onze consultants. We lopen de vervolgstappen door en kijken wat er voor jullie
          als eerste uit te halen valt.
        </p>
        <div className="flex flex-wrap gap-3 mt-6 no-print">
          <a
            href="https://spaik.io/gesprek"
            onClick={() => pushEvent('ai_discovery_book_call_click')}
            data-track-action="cta_click"
            data-track-label="ai_discovery.report.book_call"
            data-track-location="ai_discovery_report"
            data-track-dest="book_call"
            className="inline-flex items-center px-6 py-3 bg-orange-cta hover:bg-orange-hover text-white rounded-xl transition-colors"
          >
            Plan een gesprek
          </a>
          <DownloadPdfButton
            sessionId={sessionId}
            token={sessionToken}
            label="Download PDF"
            variant="outline"
            size="lg"
            className="border border-off-white/25 text-off-white hover:bg-off-white/10 hover:border-off-white/40 rounded-xl"
          />
          <button
            onClick={() => window.print()}
            className="px-6 py-3 border border-off-white/25 hover:bg-off-white/10 text-off-white rounded-xl transition-colors"
          >
            Printen
          </button>
        </div>
      </div>
    </div>
  );
}
